import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  DataGrid,
  gridClasses,
  GridEventListener,
  gridFilteredSortedRowIdsSelector,
  GridPrintGetRowsToExportParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModesModel,
  GridSortModel,
  GridToolbar,
  GridToolbarQuickFilter,
  selectedGridRowsSelector,
} from "@mui/x-data-grid";
import { alpha, styled } from "@mui/material/styles";
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "src/context/auth";
import { useMutation } from "@tanstack/react-query";
import { API_URL } from "src/utilities";
import { IGridOption } from "src/interface/IGrideOption";
const ODD_OPACITY = 0.2;
const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}));

const getSelectedRowsToExport = ({
  apiRef,
}: GridPrintGetRowsToExportParams): GridRowId[] => {
  const selectedRowIds = selectedGridRowsSelector(apiRef);
  if (selectedRowIds.size > 0) {
    return Array.from(selectedRowIds.keys());
  }

  return gridFilteredSortedRowIdsSelector(apiRef);
};

const GridViewMUIX = ({
  columns,
  rowHeight,
  loading,
  EditToolbar,
  rows = [],
  setRowModesModel,
  rowModesModel,
  processRowUpdate,
  height = "auto",
  disableColumnFilter = false,
  disableColumnSelector = false,
  disableDensitySelector = false,
  disableColumnMenu = false,
  pageSize = 10,
  sortFields,
  gridUniqueName,
  showQuickFilter = true,
  getRowHeight = "auto",
  checkboxSelection = true,
  onSelectionChange,
  keepNonExistentRowsSelected = true,
  paginationMode = "client",
  paginationModel,
  onPaginationModelChange,
  rowCount,
  onSortModelChange,
  sortModel: externalSortModel,
  onQuickFilterChange,
  quickFilterValue,
  onColumnVisibilityModelChange,
  rowSelectionModel,
}: {
  columns: any;
  rowHeight?: number;
  loading: boolean;
  checkboxSelection?: boolean;
  showQuickFilter?: boolean;
  EditToolbar?: any;
  rows?: any;
  setRowModesModel?: any;
  rowModesModel?: any;
  processRowUpdate?: any;
  height?: string | number;
  disableColumnFilter?: boolean;
  disableColumnSelector?: boolean;
  disableColumnMenu?: boolean;
  disableDensitySelector?: boolean;
  pageSize?: number;
  sortFields?: any;
  gridUniqueName: string;
  getRowHeight?: string | null;
  onSelectionChange?: (selectedIds: readonly any[]) => void;
  keepNonExistentRowsSelected?: boolean;
  paginationMode?: "client" | "server";
  paginationModel?: { page: number; pageSize: number };
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  rowCount?: number;
  onSortModelChange?: (
    model: Array<{ field: string; sort: "asc" | "desc" }>
  ) => void;
  sortModel?: Array<{ field: string; sort: "asc" | "desc" }>;
  onQuickFilterChange?: (value: string) => void;
  quickFilterValue?: string;
  onColumnVisibilityModelChange?: (model: Record<string, boolean>) => void; // برای همگام‌سازی با ExportColumnsDialog
  rowSelectionModel?: readonly any[];
}) => {
  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };
  //TODO برای همه جدول ها باید تمام ستون هاشو قرار بدی تا بتونند هر کدومشو خواستند نمایش بدند یا ندند
  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const { user } = useAuth();
  const { mutateAsync: insertAsync } = useMutation({
    mutationFn: async (body: any) => {
      const response = await fetch(API_URL + "grid-option", {
        method: "POST",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(body),
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutateAsync: getByPath } = useMutation({
    mutationFn: async (path: string) => {
      const response = await fetch(API_URL + "grid-option/by-path/" + path, {
        method: "GET",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user?.token}`,
        },
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutateAsync: updateAsync } = useMutation({
    mutationFn: async (body: { id: string | number; [key: string]: any }) => {
      const response = await fetch(`${API_URL}grid-option/${body.id}`, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json", // تغییر به application/json
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    },
    onSuccess: (responseData) => {
      // انجام اقدامات لازم پس از موفقیت
      // مثلاً: به‌روزرسانی کَش یا نمایش پیام موفقیت
    },
    onError: (error: Error) => {
      console.error("Update error:", error.message);
      // نمایش پیام خطا به کاربر، مثلاً با toast
    },
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  // استفاده از externalSortModel برای server-side pagination، در غیر این صورت از state داخلی
  const [internalSortModel, setInternalSortModel] = useState(
    sortFields ? [sortFields] : []
  );

  // State داخلی برای pagination در حالت client-side
  const [internalPaginationModel, setInternalPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({
    page: 0,
    pageSize: pageSize,
  });

  // همگام‌سازی state داخلی با تغییر pageSize از props
  useEffect(() => {
    if (paginationMode === "client" && !onPaginationModelChange) {
      setInternalPaginationModel((prev) => ({
        ...prev,
        pageSize: pageSize,
      }));
    }
  }, [pageSize, paginationMode, onPaginationModelChange]);

  // نگه داشتن callback در ref برای جلوگیری از stale closure
  const onColumnVisibilityModelChangeRef = useRef(
    onColumnVisibilityModelChange
  );
  useEffect(() => {
    onColumnVisibilityModelChangeRef.current = onColumnVisibilityModelChange;
  }, [onColumnVisibilityModelChange]);
  const sortModel =
    paginationMode === "server" && externalSortModel
      ? externalSortModel
      : internalSortModel;

  // State موقت برای جستجوی server-side (فقط برای نمایش در input)
  const [tempFilterValue, setTempFilterValue] = useState(
    quickFilterValue || ""
  );

  // Ref برای نگه داشتن focus
  const searchInputRef = useRef<HTMLInputElement>(null);

  // بازگرداندن focus به فیلد جستجو بعد از هر تغییر در tempFilterValue (keydown)
  useEffect(() => {
    if (
      paginationMode === "server" &&
      onQuickFilterChange &&
      searchInputRef.current
    ) {
      // استفاده از setTimeout برای اطمینان از اینکه DOM به‌روزرسانی شده است
      const timeoutId = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [tempFilterValue, paginationMode, onQuickFilterChange]);

  // همگام‌سازی tempFilterValue با quickFilterValue خارجی (فقط وقتی که از خارج تغییر کند)
  // استفاده از ref برای جلوگیری از re-render غیرضروری
  const prevQuickFilterValueRef = useRef(quickFilterValue);
  useEffect(() => {
    // فقط وقتی که از خارج تغییر کرده باشد (نه از تایپ کاربر)
    if (
      quickFilterValue !== undefined &&
      prevQuickFilterValueRef.current !== quickFilterValue
    ) {
      setTempFilterValue(quickFilterValue);
      prevQuickFilterValueRef.current = quickFilterValue;
    }
  }, [quickFilterValue]);

  // هندل کردن اجرای جستجو (Enter یا دکمه)
  const handleSearch = useCallback(() => {
    if (onQuickFilterChange) {
      onQuickFilterChange(tempFilterValue);
      // Reset به صفحه اول هنگام جستجو
      if (onPaginationModelChange && paginationModel) {
        onPaginationModelChange({ ...paginationModel, page: 0 });
      }
    }
  }, [
    tempFilterValue,
    onQuickFilterChange,
    onPaginationModelChange,
    paginationModel,
  ]);

  // هندل کردن کلید Enter
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  // ایجاد toolbar به صورت stable با useCallback
  // توجه: tempFilterValue به عنوان prop به toolbar پاس داده نمی‌شود
  // بلکه مستقیماً در closure استفاده می‌شود تا toolbar stable بماند
  const ToolbarComponent = useCallback(
    () => {
      // استفاده مستقیم از state در render (toolbar stable می‌ماند)
      return (
        <Stack direction={"row"} justifyContent={"space-between"}>
          <GridToolbar />
          <Stack direction={"row"} alignItems={"center"} spacing={2}>
            {EditToolbar && <EditToolbar />}
            {showQuickFilter &&
              // Server-side: جستجو با Enter یا دکمه (بدون آن‌تایم)
              (paginationMode === "server" && onQuickFilterChange ? (
                <TextField
                  key="search-field-server"
                  inputRef={searchInputRef}
                  size="small"
                  value={tempFilterValue}
                  onChange={(e) => {
                    e.stopPropagation();
                    setTempFilterValue(e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="جستجو در تمام فیلدها..."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleSearch}
                          edge="end"
                          size="small"
                          aria-label="جستجو"
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                  autoFocus={false}
                />
              ) : (
                // Client-side: استفاده از uncontrolled component (پیش‌فرض با آن‌تایم)
                <GridToolbarQuickFilter />
              ))}
          </Stack>
        </Stack>
      );
    },
    // فقط dependencies که واقعاً تغییر نمی‌کنند در اینجا هستند
    // tempFilterValue حذف شده تا toolbar stable بماند
    [
      EditToolbar,
      showQuickFilter,
      paginationMode,
      onQuickFilterChange,
      handleKeyDown,
      handleSearch,
      // tempFilterValue - حذف شده برای حفظ stability
    ]
  );

  useEffect(() => {
    async function fetchData() {
      const response = await getByPath(gridUniqueName);
      if (response?.columns) {
        const model = JSON.parse(response.columns);
        setColumnVisibilityModel(model);
        // فراخوانی callback برای همگام‌سازی با ExportColumnsDialog
        if (onColumnVisibilityModelChangeRef.current) {
          onColumnVisibilityModelChangeRef.current(model);
        }
      }
      if (response?.sorts && paginationMode !== "server") {
        setInternalSortModel(JSON.parse(response.sorts));
      }
    }
    user?.token && fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridUniqueName, user?.token]);

  const handleColumnVisibilityModelChange = async (newModel: any) => {
    try {
      const response = await getByPath(gridUniqueName);
      const body: IGridOption = {
        columns: JSON.stringify(newModel),
        pagePath: gridUniqueName,
      };
      if (response?.id) {
        await updateAsync({ ...response, columns: body.columns });
      } else {
        await insertAsync(body);
      }
    } catch (error) {
      console.error("column visibility persist error", error);
      // ادامه می‌دهیم تا UI کاربر مختل نشود
    }
    setColumnVisibilityModel(newModel);
    if (onColumnVisibilityModelChangeRef.current) {
      onColumnVisibilityModelChangeRef.current(newModel);
    }
  };

  const handleSortModelChange = async (newModel: any) => {
    try {
      const response = await getByPath(gridUniqueName);
      const body: IGridOption = {
        sotrs: JSON.stringify(newModel),
        pagePath: gridUniqueName,
      };
      if (response?.id) {
        await updateAsync({ ...response, sotrs: body.sotrs });
      } else {
        await insertAsync(body);
      }
    } catch (error) {
      console.error("sort persist error", error);
    }
    if (paginationMode !== "server") {
      setInternalSortModel(newModel);
    }
  };

  // تعیین pagination model و handler بر اساس mode
  const finalPaginationModel = useMemo(() => {
    if (paginationMode === "server") {
      // Server-side: همیشه از paginationModel خارجی استفاده کن
      return paginationModel || { page: 0, pageSize: pageSize };
    } else {
      // Client-side: اگر paginationModel خارجی پاس داده شده، از آن استفاده کن
      // در غیر این صورت از state داخلی استفاده کن
      return paginationModel || internalPaginationModel;
    }
  }, [paginationMode, paginationModel, internalPaginationModel, pageSize]);

  const handlePaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      if (paginationMode === "server") {
        // Server-side: همیشه از callback خارجی استفاده کن
        if (onPaginationModelChange) {
          onPaginationModelChange(model);
        }
      } else {
        // Client-side
        if (onPaginationModelChange) {
          // اگر callback خارجی وجود دارد، از آن استفاده کن
          onPaginationModelChange(model);
        } else {
          // در غیر این صورت از state داخلی استفاده کن
          setInternalPaginationModel(model);
        }
      }
    },
    [paginationMode, onPaginationModelChange]
  );

  return (
    <StripedDataGrid
      rows={rows}
      columns={columns}
      paginationMode={paginationMode}
      paginationModel={finalPaginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      rowCount={
        paginationMode === "server"
          ? rowCount || rows.length // Server-side: از rowCount خارجی استفاده کن
          : undefined // Client-side: undefined بگذار تا DataGrid خودش تعداد ردیف‌های فیلتر شده را محاسبه کند
      }
      initialState={{
        pagination: {
          paginationModel: {
            page: 0,
            pageSize: pageSize,
          },
        },
        sorting: sortFields
          ? { ...rows.initialState?.sorting, sortModel: [sortFields] }
          : null,
      }}
      disableDensitySelector={disableDensitySelector}
      filterMode={paginationMode === "server" ? "server" : "client"}
      disableColumnFilter={
        paginationMode === "server" ? true : disableColumnFilter
      }
      rowHeight={rowHeight}
      getRowHeight={getRowHeight === "auto" ? () => "auto" : undefined}
      editMode="row"
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
      }
      disableColumnMenu={disableColumnMenu}
      checkboxSelection={checkboxSelection}
      rowModesModel={rowModesModel}
      onRowModesModelChange={handleRowModesModelChange}
      onRowEditStop={handleRowEditStop}
      processRowUpdate={processRowUpdate}
      disableColumnSelector={disableColumnSelector}
      loading={loading}
      keepNonExistentRowsSelected={keepNonExistentRowsSelected}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={
        user?.token ? handleColumnVisibilityModelChange : undefined
      }
      sortModel={sortModel}
      onSortModelChange={
        paginationMode === "server" && onSortModelChange
          ? (model: GridSortModel) => {
              // تبدیل GridSortModel به فرمت مورد نیاز callback
              const formattedModel = model
                .filter((item) => item.sort !== null && item.sort !== undefined)
                .map((item) => ({
                  field: item.field,
                  sort: item.sort as "asc" | "desc",
                }));
              onSortModelChange(formattedModel);
            }
          : paginationMode === "client" && user?.token
            ? handleSortModelChange // Client-side: ذخیره در database
            : undefined // بدون callback: فقط client-side sort بدون ذخیره
      }
      onRowSelectionModelChange={onSelectionChange}
      rowSelectionModel={rowSelectionModel}
      slots={{
        toolbar: ToolbarComponent,
      }}
      slotProps={{
        toolbar: {
          printOptions: {
            getRowsToExport: getSelectedRowsToExport,
          },
          csvOptions: {
            utf8WithBom: true,
          },
          showQuickFilter: showQuickFilter && paginationMode !== "server", // غیرفعال کردن برای server-side
          quickFilterProps:
            paginationMode === "client"
              ? {
                  debounceMs: 500, // کاهش زمان تأخیر برای جستجو (فقط client-side)
                }
              : undefined,
        },
      }}
      pageSizeOptions={[10, 25, 50, 100]}
      disableRowSelectionOnClick
      disableVirtualization={false}
      autoHeight={false}
    />
  );
};

export default GridViewMUIX;
